/*!
VueLayers
Web map Vue components with the power of OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.10.25
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _extends = _interopDefault(require('babel-runtime/helpers/extends'));
var _Object$defineProperties = _interopDefault(require('babel-runtime/core-js/object/define-properties'));
var _Promise = _interopDefault(require('babel-runtime/core-js/promise'));
var _typeof = _interopDefault(require('babel-runtime/helpers/typeof'));
var __ol__ = _interopDefault(require('openlayers'));
var Observable = require('rxjs/Observable');
var merge = require('rxjs/observable/merge');
var distinctUntilChanged = require('rxjs/operator/distinctUntilChanged');
var map = require('rxjs/operator/map');
var throttleTime = require('rxjs/operator/throttleTime');
var Vue = _interopDefault(require('vue'));
var featuresContainer = _interopDefault(require('../mixin/features-container'));
var interactionsContainer = _interopDefault(require('../mixin/interactions-container'));
var layersContainer = _interopDefault(require('../mixin/layers-container'));
var olCmp = _interopDefault(require('../mixin/ol-cmp'));
var overlaysContainer = _interopDefault(require('../mixin/overlays-container'));
var projTransforms = _interopDefault(require('../mixin/proj-transforms'));
var collection = require('../ol-ext/collection');
var consts = require('../ol-ext/consts');
var observableFromOlEvent = _interopDefault(require('../rx-ext/from-ol-event'));
var assert = require('../util/assert');
var minilo = require('../util/minilo');
var mergeDescriptors = _interopDefault(require('../util/multi-merge-descriptors'));
var _toConsumableArray = _interopDefault(require('babel-runtime/helpers/toConsumableArray'));
var distinctUntilKeyChanged = require('rxjs/operator/distinctUntilKeyChanged');
var observableFromOlChangeEvent = _interopDefault(require('../rx-ext/from-ol-change-event'));
var _Object$assign = _interopDefault(require('babel-runtime/core-js/object/assign'));

/**
 * @vueProps
 */
var olcontrol = __ol__.control;
var VectorLayer = __ol__.layer.Vector;
var Map = __ol__.Map;
var View = __ol__.View;
var VectorSource = __ol__.source.Vector;
var props = /** @lends module:map/map# */{
  /**
   * Options for default controls added to the map by default. Set to `false` to disable all map controls. Object
   * value is used to configure controls.
   * @type {Object|boolean}
   * @todo remove when vl-control-* components will be ready
   */
  controls: {
    type: [Object, Boolean],
    default: true
  },
  /**
   * The element to listen to keyboard events on. For example, if this option is set to `document` the keyboard
   * interactions will always trigger. If this option is not specified, the element the library listens to keyboard
   * events on is the component root element.
   * @type {string|Element|Document}
   */
  keyboardEventTarget: [String, Element, Document],
  /**
   * When set to `true`, tiles will be loaded during animations.
   * @type {boolean}
   */
  loadTilesWhileAnimating: {
    type: Boolean,
    default: false
  },
  /**
   * When set to `true`, tiles will be loaded while interacting with the map.
   * @type {boolean}
   */
  loadTilesWhileInteracting: {
    type: Boolean,
    default: false
  },
  /**
   * The map logo. If a **string** is provided, it will be set as the image source of the logo. If an **object** is provided,
   * the `src` property should be the **URL** for an image and the `href` property should be a **URL** for creating a link.
   * If an **element** is provided, the **element** will be used. To disable the map logo, set the option to `false`.
   * By default, the **OpenLayers** logo is shown.
   * @type {boolean|string|Object|Element}
   */
  logo: [String, Object, Element, Boolean],
  /**
   * The minimum distance in pixels the cursor must move to be detected as a map move event instead of a click.
   * Increasing this value can make it easier to click on the map.
   * @type {Number}
   */
  moveTolerance: {
    type: Number,
    default: 1
  },
  /**
   * The ratio between physical pixels and device-independent pixels (dips) on the device.
   * @type {number}
   */
  pixelRatio: {
    type: Number,
    default: function _default() {
      return window.devicePixelRatio || 1;
    }
  },
  /**
   * Renderer. By default, **Canvas** and **WebGL** renderers are tested for support in that order,
   * and the first supported used. **Note** that the **Canvas** renderer fully supports vector data,
   * but **WebGL** can only render **Point** geometries.
   * @type {string|string[]}
   * @default ['canvas', 'webgl']
   */
  renderer: {
    type: [String, Array],
    default: function _default() {
      return [consts.RENDERER_TYPE.CANVAS, consts.RENDERER_TYPE.WEBGL];
    }
  },
  /**
   * Root element `tabindex` attribute value. Value should be provided to allow keyboard events on map.
   * @type {number|string}
   */
  tabindex: [String, Number],
  /**
   * Projection for input/output coordinates in plain data.
   * @type {string}
   */
  dataProjection: String

  /**
   * @vueComputed
   */
};var computed = {};

/**
 * @vueMethods
 */
var methods = /** @lends module:map/map# */{
  /**
   * @return {ol.Map}
   * @protected
   */
  createOlObject: function createOlObject() {
    var map$$1 = new Map({
      controls: [],
      loadTilesWhileAnimating: this.loadTilesWhileAnimating,
      loadTilesWhileInteracting: this.loadTilesWhileInteracting,
      pixelRatio: this.pixelRatio,
      renderer: this.renderer,
      logo: this.logo,
      keyboardEventTarget: this.keyboardEventTarget,
      view: this._view
    });
    // ol.Map constructor can create default view if no provided with options
    this._view = map$$1.getView();
    // add default overlay to map
    this._defaultLayer.setMap(map$$1);

    if (this.controls) {
      var opts = _typeof(this.controls) === 'object' ? this.controls : undefined;
      map$$1.getControls().extend(olcontrol.defaults(opts).getArray());
    }

    map$$1.set('dataProjection', this.dataProjection);

    return map$$1;
  },

  /**
   * @return {IndexedCollectionAdapter}
   *  @protected
   */
  getLayersTarget: function getLayersTarget() {
    assert.hasMap(this);

    if (this._layersTarget == null) {
      this._layersTarget = new collection.IndexedCollectionAdapter(this.$map.getLayers(), function (layer) {
        return layer.get('id');
      });
    }

    return this._layersTarget;
  },

  /**
   * @return {IndexedCollectionAdapter}
   * @protected
   */
  getInteractionsTarget: function getInteractionsTarget() {
    assert.hasMap(this);

    if (this._interactionsTarget == null) {
      this._interactionsTarget = new collection.IndexedCollectionAdapter(this.$map.getInteractions(), function (interaction) {
        return interaction.get('id');
      });
    }

    return this._interactionsTarget;
  },

  /**
   * @return {function}
   * @protected
   */
  getDefaultInteractionsSorter: function getDefaultInteractionsSorter() {
    // sort interactions by priority in asc order
    // the higher the priority, the earlier the interaction handles the event
    return function (a, b) {
      var ap = a.get('priority') || 0;
      var bp = b.get('priority') || 0;
      return ap === bp ? 0 : ap - bp;
    };
  },

  /**
   * @return {SourceCollectionAdapter}
   * @protected
   */
  getFeaturesTarget: function getFeaturesTarget() {
    if (this._featuresTarget == null) {
      this._featuresTarget = new collection.SourceCollectionAdapter(this._defaultLayer.getSource());
    }

    return this._featuresTarget;
  },

  /**
   * @return {IndexedCollectionAdapter}
   * @protected
   */
  getOverlaysTarget: function getOverlaysTarget() {
    assert.hasMap(this);

    if (this._overlaysTarget == null) {
      this._overlaysTarget = new collection.IndexedCollectionAdapter(this.$map.getOverlays(), function (overlay) {
        return overlay.getId();
      });
    }

    return this._overlaysTarget;
  },

  /**
   * @param {number[]} pixel
   * @return {number[]} Coordinates in the map view projection.
   */
  getCoordinateFromPixel: function getCoordinateFromPixel(pixel) {
    assert.hasMap(this);

    var coordinate = this.$map.getCoordinateFromPixel(pixel);
    return this.pointToDataProj(coordinate);
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    var vm = this;

    return mergeDescriptors(olCmp.methods.getServices.call(this), layersContainer.methods.getServices.call(this), interactionsContainer.methods.getServices.call(this), overlaysContainer.methods.getServices.call(this), featuresContainer.methods.getServices.call(this), {
      get map() {
        return vm.$map;
      },
      get view() {
        return vm.$view;
      },
      get viewContainer() {
        return vm;
      }
    });
  },

  /**
   * Triggers focus on map container.
   * @return {void}
   */
  focus: function focus() {
    this.$el.focus();
  },

  /**
   * @param {number[]} pixel
   * @param {function((ol.Feature|ol.render.Feature), ?ol.layer.Layer): *} callback
   * @param {Object} [opts]
   * @return {*|undefined}
   */
  forEachFeatureAtPixel: function forEachFeatureAtPixel(pixel, callback) {
    var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    assert.hasMap(this);
    return this.$map.forEachFeatureAtPixel(pixel, callback, opts);
  },

  /**
   * @param {number[]} pixel
   * @param {function(ol.layer.Layer, ?(number[]|Uint8Array)): *} callback
   * @param {function(ol.layer.Layer): boolean} [layerFilter]
   * @return {*|undefined}
   */
  forEachLayerAtPixel: function forEachLayerAtPixel(pixel, callback, layerFilter) {
    assert.hasMap(this);
    return this.$map.forEachLayerAtPixel(pixel, callback, undefined, layerFilter);
  },

  /**
   * @param {ol.View|Vue|undefined} view
   * @return {void}
   * @protected
   */
  setView: function setView(view) {
    view = view instanceof Vue ? view.$view : view;
    view || (view = new View());

    if (view !== this._view) {
      this._view = view;
    }
    if (this.$map && view !== this.$map.getView()) {
      this.$map.setView(view);
    }
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    assert.hasMap(this);
    this.$map.setTarget(this.$el);
    this.subscribeAll();
    this.updateSize();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    assert.hasMap(this);
    this.unsubscribeAll();
    this.$map.setTarget(undefined);
  },

  /**
   * Updates map size and re-renders map.
   * @return {Promise}
   */
  refresh: function refresh() {
    var _this = this;

    this.updateSize();

    return this.render().then(function () {
      return olCmp.methods.refresh.call(_this);
    });
  },

  /**
   * @return {Promise}
   */
  render: function render() {
    var _this2 = this;

    return new _Promise(function (resolve) {
      assert.hasMap(_this2);
      _this2.$map.once('postrender', function () {
        return resolve();
      });
      _this2.$map.render();
    });
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToMapEvents.call(this);
  },

  /**
   * Updates map size.
   * @return {void}
   */
  updateSize: function updateSize() {
    assert.hasMap(this);
    this.$map.updateSize();
  }
};

var watch = {
  dataProjection: function dataProjection(value) {
    if (this.$map) {
      this.$map.set('dataProjection', value);
      this.scheduleRefresh();
    }
  }
};

/**
 * Container for **layers**, **interactions**, **controls** and **overlays**. It responsible for viewport
 * rendering and low level interaction events.
 *
 * @title vl-map
 * @alias module:map/map
 * @vueProto
 *
 * @fires module:map/map#click
 * @fires module:map/map#dblclick
 * @fires module:map/map#singleclick
 * @fires module:map/map#pointerdrag
 * @fires module:map/map#pointermove
 * @fires module:map/map#movestart
 * @fires module:map/map#moveend
 * @fires module:map/map#postrender
 * @fires module:map/map#precompose
 * @fires module:map/map#postcompose
 *
 * @vueSlot default Default slot for all child components.
 */
var Map$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('div', { class: [_vm.$options.name], attrs: { "tabindex": _vm.tabindex } }, [_vm._t("default")], 2);
  }, staticRenderFns: [],
  name: 'vl-map',
  mixins: [olCmp, layersContainer, interactionsContainer, overlaysContainer, featuresContainer, projTransforms],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  created: function created() {
    var _this3 = this;

    /**
     * @type {ol.View|undefined}
     * @private
     */
    this._view = undefined;
    /**
     * @type {ol.layer.Vector}
     * @private
     */
    this._defaultLayer = new VectorLayer({
      source: new VectorSource()
    });

    _Object$defineProperties(this, /** @lends module:map/map# */{
      /**
       * OpenLayers map instance.
       * @type {ol.Map|undefined}
       */
      $map: {
        enumerable: true,
        get: function get() {
          return _this3.$olObject;
        }
      },
      /**
       * OpenLayers view instance.
       * @type {ol.View|undefined}
       */
      $view: {
        enumerable: true,
        get: function get() {
          return _this3._view;
        }
      }
    });
  }
};

/**
 * Subscribe to OL map events.
 *
 * @return {void}
 * @private
 */
function subscribeToMapEvents() {
  var _context,
      _this4 = this;

  assert.hasMap(this);
  assert.hasView(this);

  var ft = 100;
  // pointer
  var pointerEvents = (_context = merge.merge.call(Observable.Observable, observableFromOlEvent(this.$map, ['click', 'dblclick', 'singleclick']), (_context = (_context = observableFromOlEvent(this.$map, ['pointerdrag', 'pointermove']), throttleTime.throttleTime).call(_context, ft), distinctUntilChanged.distinctUntilChanged).call(_context, function (a, b) {
    return minilo.isEqual(a.coordinate, b.coordinate);
  })), map.map).call(_context, function (evt) {
    return _extends({}, evt, {
      coordinate: _this4.pointToDataProj(evt.coordinate)
    });
  });
  // other
  var otherEvents = observableFromOlEvent(this.$map, ['movestart', 'moveend', 'postrender', 'precompose', 'postcompose']);

  var events = merge.merge.call(Observable.Observable, pointerEvents, otherEvents);

  this.subscribeTo(events, function (evt) {
    return _this4.$emit(evt.type, evt);
  });
}

/**
 * A click with no dragging. A double click will fire two of this.
 * @event module:map/map#click
 * @type {ol.MapBrowserEvent}
 */
/**
 * A true double click, with no dragging.
 * @event module:map/map#dblclick
 * @type {ol.MapBrowserEvent}
 */

/**
 * @vueProps
 */
var View$1 = __ol__.View;
var props$1 = /** @lends module:map/view# */{
  /**
   * The center coordinate in the view projection.
   * @type {number[]}
   * @default [0, 0]
   * @vueSync
   */
  center: {
    type: Array,
    default: function _default() {
      return [0, 0];
    },
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  constrainRotation: {
    type: [Boolean, Number],
    default: true
  },
  enableRotation: {
    type: Boolean,
    default: true
  },
  /**
   * The extent that constrains the center defined in the view projection,
   * in other words, center cannot be set outside this extent.
   * @default undefined
   */
  extent: {
    type: Array,
    validator: function validator(value) {
      return value.length === 4;
    }
  },
  maxResolution: Number,
  minResolution: Number,
  /**
   * @default 28
   */
  maxZoom: {
    type: Number,
    default: consts.MAX_ZOOM
  },
  /**
   * @default 0
   */
  minZoom: {
    type: Number,
    default: consts.MIN_ZOOM
  },
  /**
   * @type {string}
   * @default EPSG:3857
   */
  projection: {
    type: String,
    default: consts.EPSG_3857
  },
  resolution: Number,
  resolutions: Array,
  /**
   * The initial rotation for the view in **radians** (positive rotation clockwise).
   * @type {number}
   * @vueSync
   */
  rotation: {
    type: Number,
    default: 0
  },
  /**
   * Zoom level used to calculate the resolution for the view as `int` value. Only used if `resolution` is not defined.
   * @type {number}
   * @default 0
   * @vueSync
   */
  zoom: {
    type: Number,
    default: consts.MIN_ZOOM
  },
  /**
   * @default 2
   */
  zoomFactor: {
    type: Number,
    default: consts.ZOOM_FACTOR
  }

  /**
   * @vueComputed
   */
};var computed$1 = /** @lends module:map/view# */{
  viewZoom: function viewZoom() {
    if (this.rev && this.$view) {
      return Math.round(this.$view.getZoom());
    }

    return this.zoom;
  },
  viewRotation: function viewRotation() {
    if (this.rev && this.$view) {
      return this.$view.getRotation();
    }

    return this.rotation;
  },
  viewResolution: function viewResolution() {
    if (this.rev && this.$view) {
      return this.$view.getResolution();
    }

    return this.resolution;
  },
  viewCenter: function viewCenter() {
    if (this.rev && this.$view) {
      return this.pointToDataProj(this.$view.getCenter());
    }
  },
  viewCenterViewProj: function viewCenterViewProj() {
    if (this.rev && this.$view) {
      return this.$view.getCenter();
    }
  },

  /**
   * @return {ol.ProjectionLike}
   */
  resolvedDataProjection: function resolvedDataProjection() {
    // exclude this.projection from lookup to allow view rendering in projection
    // that differs from data projection
    return minilo.coalesce(this.$viewContainer && this.$viewContainer.resolvedDataProjection, this.$options.dataProjection, this.viewProjection);
  }
};

/**
 * @vueMethods
 */
var methods$1 = /** @lends module:map/view# */{
  /**
   * @see {@link https://openlayers.org/en/latest/apidoc/ol.View.html#animate}
   * @param {...(olx.AnimationOptions|function(boolean))} args
   * @return {Promise} Resolves when animation completes
   */
  animate: function animate() {
    var _this = this;

    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    assert.hasView(this);

    var cb = minilo.noop;
    if (minilo.isFunction(args[args.length - 1])) {
      cb = args[args.length - 1];
      args = args.slice(0, args.length - 1);
    }
    args.forEach(function (opts) {
      if (!Array.isArray(opts.center)) return;
      opts.center = _this.pointToViewProj(opts.center);
    });

    return new _Promise(function (resolve) {
      var _$view;

      return (_$view = _this.$view).animate.apply(_$view, _toConsumableArray(args).concat([function (complete) {
        cb(complete);
        resolve(complete);
      }]));
    });
  },

  /**
   * @return {ol.View}
   * @protected
   */
  createOlObject: function createOlObject() {
    return new View$1({
      center: this.pointToViewProj(this.center),
      constrainRotation: this.constrainRotation,
      enableRotation: this.enableRotation,
      extent: this.extent ? this.extentToViewProj(this.extent) : undefined,
      maxResolution: this.maxResolution,
      minResolution: this.minResolution,
      maxZoom: this.maxZoom,
      minZoom: this.minZoom,
      projection: this.projection,
      resolution: this.resolution,
      resolutions: this.resolutions,
      rotation: this.rotation,
      zoom: this.zoom,
      zoomFactor: this.zoomFactor
    });
  },

  /**
   * @see {@link https://openlayers.org/en/latest/apidoc/ol.View.html#fit}
   * @param {GeoJSONFeature|ol.Extent|ol.geom.Geometry|Vue} geometryOrExtent
   * @param {olx.view.FitOptions} [options]
   * @return {Promise} Resolves when view changes
   */
  fit: function fit(geometryOrExtent) {
    var _this2 = this;

    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    assert.hasView(this);

    // transform from GeoJSON, vl-feature to ol.Feature
    if (minilo.isPlainObject(geometryOrExtent)) {
      geometryOrExtent = this.readGeometryInDataProj(geometryOrExtent);
    } else if (geometryOrExtent instanceof Vue) {
      geometryOrExtent = geometryOrExtent.$geometry;
    }

    var cb = options.callback || minilo.noop;

    return new _Promise(function (resolve) {
      _this2.$view.fit(geometryOrExtent, _extends({}, options, {
        callback: function callback(complete) {
          cb(complete);
          resolve(complete);
        }
      }));
    });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    this.$viewContainer && this.$viewContainer.setView(this);
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unsubscribeAll();
    this.$viewContainer && this.$viewContainer.setView(undefined);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToViewChanges.call(this);
  }
};

var watch$1 = {
  center: function center(value) {
    value = this.pointToViewProj(value);
    if (this.$view && !this.$view.getAnimating() && !minilo.isEqual(value, this.viewCenterViewProj)) {
      this.$view.setCenter(value);
    }
  },
  resolution: function resolution(value) {
    if (this.$view && !this.$view.getAnimating() && value !== this.viewResolution) {
      this.$view.setResolution(value);
    }
  },
  zoom: function zoom(value) {
    value = Math.round(value);
    if (this.$view && !this.$view.getAnimating() && value !== this.viewZoom) {
      this.$view.setZoom(value);
    }
  },
  rotation: function rotation(value) {
    if (this.$view && !this.$view.getAnimating() && value !== this.viewRotation) {
      this.$view.setRotation(value);
    }
  },
  minZoom: function minZoom(value) {
    if (this.$view && value !== this.$view.getMinZoom()) {
      this.$view.setMinZoom(value);
    }
  },
  maxZoom: function maxZoom(value) {
    if (this.$view && value !== this.$view.getMaxZoom()) {
      this.$view.setMaxZoom(value);
    }
  },
  resolvedDataProjection: function resolvedDataProjection() {
    if (this.$view) {
      this.$view.setCenter(this.pointToViewProj(this.center));
    }
  }
};

/**
 * Represents a simple **2D view** of the map. This is the component to act upon to change the **center**,
 * **resolution**, and **rotation** of the map.
 *
 * @title View `vl-view` component
 * @alias module:map/view
 * @vueProto
 *
 * @vueSlot default [scoped] Default scoped slot with current state: center, zoom, rotation & etc.
 */
var View$2 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('i', { class: [_vm.$options.name], staticStyle: { "display": "none !important" } }, [_vm._t("default", null, { center: _vm.viewCenter, zoom: _vm.viewZoom, resolution: _vm.viewResolution, rotation: _vm.viewRotation })], 2);
  }, staticRenderFns: [],
  name: 'vl-view',
  mixins: [olCmp, projTransforms],
  props: props$1,
  computed: computed$1,
  methods: methods$1,
  watch: watch$1,
  stubVNode: {
    empty: function empty() {
      return this.$options.name;
    }
  },
  /**
   * @this module:map/view
   */
  created: function created() {
    var _this3 = this;

    _Object$defineProperties(this, /** @lends module:map/view# */{
      /**
       * @type {ol.View|undefined}
       */
      $view: {
        enumerable: true,
        get: function get() {
          return _this3.$olObject;
        }
      },
      $viewContainer: {
        enumerable: true,
        get: function get() {
          return _this3.$services && _this3.$services.viewContainer;
        }
      }
    });
  }
};

/**
 * Subscribe to OpenLayers significant events
 * @return {void}
 * @private
 */
function subscribeToViewChanges() {
  var _context,
      _this4 = this;

  assert.hasView(this);

  var ft = 1000 / 60;
  var resolution = observableFromOlChangeEvent(this.$view, 'resolution', true, ft);
  var zoom = (_context = map.map.call(resolution, function () {
    return {
      prop: 'zoom',
      value: Math.round(_this4.$view.getZoom())
    };
  }), distinctUntilKeyChanged.distinctUntilKeyChanged).call(_context, 'value');

  var changes = merge.merge.call(Observable.Observable, observableFromOlChangeEvent(this.$view, 'center', true, ft, function () {
    return _this4.pointToDataProj(_this4.$view.getCenter());
  }), observableFromOlChangeEvent(this.$view, 'rotation', true, ft), resolution, zoom);

  this.subscribeTo(changes, function (_ref) {
    var prop = _ref.prop,
        value = _ref.value;

    ++_this4.rev;
    _this4.$emit('update:' + prop, value);
  });
}

/**
 * @alias module:map
 *
 * @example Install
 * import Vue from 'vue'
 * // import module and styles
 * import { Map } from 'vuelayers'
 * // or
 * import Map from 'vuelayers/lib/map'
 * // import VueLayers styles
 * import 'vuelayers/lib/style.css'
 * // register vl-map and vl-view components
 * Vue.use(Map)
 *
 * @vueExample Usage [html]
 * <vl-map :load-tiles-while-animating="true" :load-tiles-while-interacting="true" style="height: 400px">
 *   <vl-view :center="[0, 0]" :zoom="3" :rotation="0"></vl-view>
 *
 *   <vl-layer-tile>
 *     <vl-source-osm></vl-source-osm>
 *   </vl-layer-tile>
 * </vl-map>
 */
var index = {
  /**
   * Container for **layers**, **interactions**, **controls** and **overlays**. It responsible for viewport
   * rendering and low level interaction events.
   * @alias module:map/map
   */
  Map: Map$1,
  /**
   * Represents a simple **2D view** of the map. This is the component to act upon to change the **center**,
   * **resolution**, and **rotation** of the map.
   * @alias module:map/map
   */
  View: View$2,
  /**
   * Registers Map and View components with default names.
   *
   * @param {Vue} Vue
   * @param {VueLayersOptions} [options]
   * @return {void}
   */
  install: function install(Vue$$1) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    options = minilo.pick(options, 'dataProjection');
    _Object$assign(Map$1, options);
    _Object$assign(View$2, options);

    Vue$$1.component(Map$1.name, Map$1);
    Vue$$1.component(View$2.name, View$2);
  }
};

module.exports = index;



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/map/index.js
// module id = 1469
// module chunks = 1